import { useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DashboardCard, { EmptyState } from './DashboardCard';
import CardGrid from './CardGrid';
import { CARD_FILL_MIN_WIDTH, cardDateColumn, type CardColumn } from './cardColumns';
import type { DashboardMealPatternNote } from '../../types/dashboard';
import { useDashboardNav, RESIDENT_TAB } from './useDashboardNav';

const columns: CardColumn<DashboardMealPatternNote>[] = [
  cardDateColumn<DashboardMealPatternNote>('note_Date', 'Date', { sortOrder: 'desc' }),
  { dataField: 'fullName', caption: 'Name', sizing: 'fill' },
  { dataField: 'note_Title', caption: 'Title', sizing: 'fill', minWidth: CARD_FILL_MIN_WIDTH },
];

interface Props {
  data: DashboardMealPatternNote[];
  allData?: DashboardMealPatternNote[];
  onShowAllChange?: (showAll: boolean) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export default function MealPatternNotesCard({ data, allData, onShowAllChange, ...dragProps }: Props) {
  const { rowLinkProps } = useDashboardNav();
  const [showAll, setShowAll] = useState(false);

  const displayData = showAll && allData ? allData : data;

  const handleShowAllToggle = (checked: boolean) => {
    setShowAll(checked);
    onShowAllChange?.(checked);
  };

  // Show All lives in the card header (right-anchored via the
  // DashboardCard `headerRight` slot) so we don't need a separate
  // toolbar band above the grid. Reduced padding + smaller label
  // font keep it compact inside the ~28px-tall header row.
  const showAllControl = (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={showAll}
          onChange={(e) => handleShowAllToggle(e.target.checked)}
          sx={{ p: 0.25 }}
        />
      }
      label="Show All"
      sx={{ m: 0 }}
      slotProps={{ typography: { fontSize: '0.8rem' } }}
    />
  );

  return (
    <DashboardCard
      icon="bi-journal-text"
      title="Meal Pattern Notes Not Completed"
      count={displayData.length}
      headerRight={showAllControl}
      {...dragProps}
    >
      {displayData.length === 0 ? (
        <EmptyState message="No incomplete meal pattern notes" />
      ) : (
        <CardGrid<DashboardMealPatternNote>
          {...rowLinkProps<DashboardMealPatternNote>((r) => r.fKey_Patient, RESIDENT_TAB.mealPattern)}
          ariaLabel="Meal Pattern Notes Not Completed"
          dataSource={displayData}
          columns={columns}
          // A resident can have more than one open note.
          keyExpr={(r) => `${r.fKey_Patient}|${r.note_Date}|${r.note_Title}`}
        />
      )}
    </DashboardCard>
  );
}
